<template>
  <nav id="navbar">
    <div id="navbar__container">
      <div id="navbar__logo-container">
        <router-link
          to="/"
          class="navbar__section_home"
          href="#"
          :title="$t('tooltips.HOME')"
          :aria-label="$t('accessibility.ARIA_HOME')"
        >
          <img
            id="navbar__logo"
            src="@/assets/logo.svg"
            alt="Ifrågasätt logo"
          >
        </router-link>
      </div>
      <div
        id="navbar__menu-container"
        v-if="isLoggedIn"
      >
        <button
          v-if="mobile && !open"
          class="navbar__section"
          @click="toggleMenu"
        >
          <Icon
            id="navbar__open"
            class="navbar__icon"
            name="menu"
          />
          <p class="navbar__section_text">
            <span class="large">{{ $t("general.MENU") }}</span>
          </p>
        </button>
        <transition name="fade">
          <div
            class="backdrop"
            v-if="mobile && open"
            @click="toggleMenu"
          />
        </transition>
        <transition name="slide-fade">
          <section
            v-show="open"
            id="navbar__menu"
          >
            <button
              v-if="mobile"
              class="navbar__section"
              @click="toggleMenu"
              :title="$t('tooltips.CLOSE_MENU')"
              :aria-label="$t('accessibility.ARIA_CLOSE_MENU')"
            >
              <Icon
                class="navbar__icon"
                name="close"
                :fills="['#fff', 'none']"
              />
              <p class="navbar__section_text">
                <span class="large">{{ $t("tooltips.CLOSE_MENU") }}</span>
              </p>
            </button>
            <router-link
              v-if="mobile"
              :to="{ path: '/', query: { pageSize: 10, offset: 0 } }"
              class="navbar__section"
              href="#"
              :title="$t('tooltips.HOME')"
              :aria-label="$t('accessibility.ARIA_HOME')"
            >
              <Icon
                class="navbar__icon"
                name="home"
                :fills="['#fff', '#fff']"
              />
              <p class="navbar__section_text">
                <span class="large">{{ $t("tooltips.HOME") }}</span>
              </p>
            </router-link>
            <router-link
              :to="{ path: 'comments', query: { pageSize: 10, offset: 0 } }"
              class="navbar__section"
              href="#"
              :title="$t('tooltips.COMMENTS')"
              :aria-label="$t('accessibility.ARIA_COMMENTS')"
            >
              <Icon
                class="navbar__icon"
                name="comment"
                :fills="['#fff', '#fff']"
              />
              <p class="navbar__section_text">
                <span class="large">{{ $t("tooltips.COMMENTS") }}</span>
              </p>
            </router-link>
            <router-link
              to="/profile"
              class="navbar__section"
              href="#"
              :title="$t('tooltips.PROFILE')"
              :aria-label="$t('accessibility.ARIA_PROFILE')"
            >
              <Icon
                class="navbar__icon"
                name="user"
                :fills="['#fff', 'none']"
              />
              <p class="navbar__section_text">
                <span class="large">{{ $t("tooltips.PROFILE") }}</span>
              </p>
            </router-link>
            <router-link
              to="/alias"
              class="navbar__section"
              href="#"
              :title="$t('tooltips.ALIAS')"
              :aria-label="$t('accessibility.ARIA_ALIAS')"
            >
              <Icon
                class="navbar__icon"
                name="alias"
                :fills="['#fff']"
              />
              <p class="navbar__section_text">
                <span class="large">{{ $t("tooltips.ALIAS") }}</span>
              </p>
            </router-link>
            <router-link
              to="/settings"
              class="navbar__section"
              href="#"
              :title="$t('tooltips.SETTINGS')"
              :aria-label="$t('accessibility.ARIA_SETTINGS')"
            >
              <Icon
                class="navbar__icon"
                name="settings"
                :fills="['#fff']"
              />
              <p class="navbar__section_text">
                <span class="large">{{ $t("tooltips.SETTINGS") }}</span>
              </p>
            </router-link>
            <template v-if="mobile">
              <a
                class="navbar__section"
                v-if="myProfile.isDirektUser"
                :href="direktUrl"
              >
                <Icon
                  class="navbar__icon"
                  name="direkt"
                  :fills="['#fff']"
                />
                <p class="navbar__section_text">{{ $t("general.DIREKT") }}</p>
              </a>
              <a
                class="navbar__section"
                v-if="myProfile.isModOrAdmin"
                :href="modUrl"
              >
                <Icon
                  class="navbar__icon"
                  name="moderator"
                  :fills="['#fff']"
                />
                <p class="navbar__section_text">
                  {{ $t("general.MODERATOR") }}
                </p>
              </a>
              <button
                class="navbar__section"
                @click="loggingOut = true"
                :title="$t('general.LOGOUT')"
                :aria-label="$t('accessibility.ARIA_LOGOUT')"
              >
                <Icon
                  class="navbar__icon"
                  name="logout"
                  :fills="['#fff']"
                />
                <p class="navbar__section_text">
                  <span class="large">{{ $t("general.LOGOUT") }}</span>
                </p>
              </button>
            </template>
            <DropDown
              v-else
              @logOut="showLogOutModal"
            />
          </section>
        </transition>
      </div>
    </div>
    <Modal
      v-if="loggingOut"
      @close="closeModal"
      :heading="'general.LOGOUT'"
      :subject="'user.LOGOUT_MODAL'"
    >
      <template v-slot:modal-buttons>
        <SpinnerButton
          :button-id="'btn__logout'"
          :button-class="'btn__warning'"
          :button-type="'submit'"
          :button-listener="signOutUser"
          :button-text="$t('general.LOGOUT')"
        />
      </template>
    </Modal>
  </nav>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DropDown from "./DropDown";
import Icon from "./Icon.vue";
import Modal from "./Modal";
import SpinnerButton from "./SpinnerButton";

export default {
  name: "NavBar",
  components: {
    DropDown,
    Icon,
    Modal,
    SpinnerButton,
  },
  data() {
    return {
      open: false,
      mobile: false,
      loggingOut: false,
    };
  },
  created() {
    this.isMobile();
    window.addEventListener("resize", this.isMobile);
  },
  computed: {
    ...mapGetters(["myProfile", "isLoggedIn"]),
    direktUrl() {
      return process.env.VUE_APP_DIREKT_ROUTE;
    },
    modUrl() {
      return process.env.VUE_APP_MOD_ROUTE;
    },
  },
  watch: {
    $route() {
      if (this.mobile) {
        this.open = false;
      }
    },
    loggingOut() {
      if (this.loggingOut && this.mobile) {
        this.open = false;
      }
    },
  },
  methods: {
    ...mapActions(["toggleModal", "signOutUser"]),
    closeModal() {
      this.loggingOut = false;
    },
    isMobile() {
      const mq = window.matchMedia(
        "only screen and (max-width: 1023px)"
      ).matches;

      if (!mq) {
        this.open = true;
      } else {
        this.open = false;
      }

      this.mobile = mq;
    },
    showLogOutModal() {
      this.loggingOut = true;
    },
    toggleMenu() {
      this.open = !this.open;
    },
  },
};
</script>
